// ©2024 Austin App House. All rights reserved.
//
// Bank payment API helpers (frontend contract).
//
// POST /api/payment/save-bank
//   body: { accountHolderName, routingNumber, accountNumber, accountType, bankName }
//   res:  { success, message, _id?, paymentMethodId? }
//
// POST /api/payment/add-bank-account  (Converge — Add Funds)
//   new bank: { amount, bankName, accountHolderName, accountType, routingNumber,
//              accountNumber, email?, saveBankAccount?, returnUrl? }
//   saved bank: { paymentMethodId: string, amount: number }
//   res:  charge result; may include nextAction.verify_with_microdeposits
//
// GET /api/payment/payment-methods
//   res:  { savedBankAccounts?: [...], bankPaymentMethods?: [...] }

import axios from 'axios';

export const PAYMENT_SAVE_BANK = '/api/payment/save-bank';
export const PAYMENT_ADD_BANK_ACCOUNT = '/api/payment/add-bank-account';

export function getSavedBankId(record) {
  return record?._id || record?.id || record?.paymentMethodId;
}

/** Options for saved-bank dropdowns (Add Funds, etc.). */
export function mapSavedBanksForSelect(res) {
  const data = res?.data ?? res;
  const banks = [
    ...(data.savedBankAccounts || []),
    ...(data.bankPaymentMethods || []),
  ];

  return banks.map((el) => {
    const bank = el.us_bank_account || el;
    const bankName = el.bankName || bank.bank_name || 'Bank';
    const last4 = el.last4 || bank.last4;
    const id = getSavedBankId(el);
    return {
      label: `${bankName} ending in: ${last4}`,
      value: id,
      paymentMethodId: id,
      type: 'bank',
      provider: el.provider || 'bank',
    };
  });
}

export async function saveBankAccount({
  accountHolderName,
  routingNumber,
  accountNumber,
  accountType,
  bankName,
  saveForLater,
}) {
  const body = {
    accountHolderName,
    routingNumber,
    accountNumber,
    accountType,
    bankName,
  };
  if (saveForLater !== undefined) {
    body.saveForLater = Boolean(saveForLater);
  }
  const { data } = await axios.post(PAYMENT_SAVE_BANK, body);
  return data;
}

export async function chargeSavedBankAccount({ paymentMethodId, amount }) {
  const { data } = await axios.post(PAYMENT_ADD_BANK_ACCOUNT, {
    paymentMethodId,
    amount: Number(amount),
  });
  return data;
}

/** Add funds with a new bank account via Converge Pay (single request). */
export async function addBankAccountAndFund({
  amount,
  bankName,
  accountHolderName,
  accountType,
  routingNumber,
  accountNumber,
  email,
  saveBankAccount,
  returnUrl,
}) {
  const { data } = await axios.post(PAYMENT_ADD_BANK_ACCOUNT, {
    amount: Number(amount),
    bankName,
    accountHolderName,
    accountType,
    routingNumber,
    accountNumber,
    email,
    saveBankAccount: Boolean(saveBankAccount),
    returnUrl,
  });
  return data;
}

/** Normalize saved banks for the Payment Methods list UI. */
export function mapSavedBanksForList(res) {
  const data = res?.data ?? res;
  const banks = [
    ...(data.savedBankAccounts || []),
    ...(data.bankPaymentMethods || []),
  ];

  return banks.map((el) => {
    const bank = el.us_bank_account || el;
    const id = getSavedBankId(el);
    return {
      ...el,
      id,
      type: 'bank',
      card: {
        brand: el.bankName || bank.bank_name || 'Bank',
        last4: el.last4 || bank.last4,
      },
    };
  });
}
